'use client';

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { ROUTES } from "@/lib/constants";

interface VerifyOTPFormProps {
  email: string;
  onBack: () => void;
  redirectTo?: string;
}

export function VerifyOTPForm({ email, onBack, redirectTo }: VerifyOTPFormProps) {
  const [otp, setOTP] = useState('');
  const { verifyOTP, verifyState } = useAuth();
  const router = useRouter();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    
    try {
      // verifyOTP will throw an error if verification fails
      // which will be caught and stored in verifyState
      await verifyOTP({ email, otp });
      // If we get here, verification was successful
      // Redirect to the intended page or dashboard
      router.push(redirectTo || ROUTES.DASHBOARD);
    } catch {
      // Error is handled in verifyState
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="email-display" className="text-sm font-medium text-foreground">
          Email
        </Label>
        <Input
          id="email-display"
          value={email}
          disabled
          className="h-11 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-muted-foreground"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="otp" className="text-sm font-medium text-foreground">
          OTP Code
        </Label>
        <Input 
          id="otp"
          name="otp"
          value={otp}
          onChange={(e) => setOTP(e.target.value)}
          placeholder="Enter 6-digit OTP" 
          type="text"
          maxLength={6}
          className="h-11 border-gray-200 dark:border-gray-600 focus:border-orange-500 focus:ring-orange-500/20 transition-colors text-center tracking-widest font-mono text-lg"
          required
        />
      </div>

      {/* Display error from context state */}
      {verifyState.error && (
        <div className="pt-2">
          <p className="text-sm text-red-500">{verifyState.error}</p>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button 
          type="button" 
          variant="outline" 
          className="flex-1 h-12 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" 
          onClick={onBack}
        >
          Back
        </Button>
        <Button 
          type="submit" 
          className="flex-1 h-12 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200" 
          disabled={verifyState.isLoading}
          size="lg"
        >
          {verifyState.isLoading ? "Verifying..." : "Verify OTP"}
        </Button>
      </div>
    </form>
  );
} 